/**
 * Group "battle": what a PvE / assault battle costs and what a won battle gives (design D2 battle.ts, D4).
 *
 *  - applyBattleStart = FWP.prepare_game (tiredness, attempts, stims taken into the battle, achiev.stims, task 6 -> 7)
 *    + the net_refresh_time of net_check_auc (./battle-time.ts) + buildBattleTeam (what init_game hands to
 *    proc.init_gameplay) + the battle seed (proc.init_gameplay: cur_random = true_random(0, 399)).
 *    Preconditions: the conditions under which the 'fight' screen shows 'go' (new_interf.render "fight") and the
 *    assault screen shows 'assault_fight' (new_interf.render "assault").
 *  - applyBattleResult = FWP.end_game_fn for a finished battle (rewards of a PvE or assault win, nothing for a loss,
 *    a cancelled battle or a 'just' battle) + check_new_level (./battle-level.ts). The player inputs of the reward
 *    (difficulty, perks, spec, the stage progress) come from the BattleRewardBasis the start returned, not from the
 *    state at the finish: the original player could change none of them during a battle (every menu is closed), while
 *    the backend accepts other commands between battle.start and battle.result.
 *
 * The original flow: 'go' (new_interf.click) -> prepare_game -> map load -> continue_preparing_game ->
 * net_check_auc -> net_refresh_time -> net_save('battle_start') -> init_game -> proc.init_gameplay; the battle ends in
 * proc -> end_game_fn(winner) -> (win) init_save_params -> net_save('battle_end') -> init_win_or_lose.
 *
 * These are NOT player commands: battle.start needs server facts (the assault zone's stage and owner, the auction
 * annihilators) and the backend keeps the in-flight battle (seed, stims, pre-battle state, the reward basis) next to
 * the state; battle.result takes the outcome the battle validator verified and that battle's reward basis. The
 * backend runs them itself; they are registered in SERVER_RULE_OPS (ops/index.ts), never in RULE_OPS, so
 * player.command cannot reach them.
 */
import { argsObject, intArg, optionalIntArg, optionalStringArg, stringArg } from '../args';
import { clone, int, keys } from '../as3';
import type { RuleContext } from '../context';
import type { GameDefs } from '../defs';
import { check, RuleError } from '../errors';
import { defineOp, type OpOutput } from '../registry';
import type { PlayerState, WorkingState } from '../state';
import { affectedSkills, battleTeamDef, hardcoreMulti, perkEffect, ratingInc, skillMulti, type AffectedSkills, type StageMode } from './battle-formulas';
import { checkNewLevel, RATING_PER_LEVEL, type BattleGrave, type EndGame } from './battle-level';
import { refreshTime } from './battle-time';

export * from './battle-formulas';
export * from './battle-level';
export * from './battle-time';

// ------------------------------------------------------------------------------------------------ constants

/** FWP.TIRED_INC: tiredness of one battle (before the less_tired perk / spec 3). */
export const TIRED_INC = 10;
/** FWP.TIRED_THRESHOLD: from this tiredness on (stims included) the 'fight' screen hides 'go'. */
export const TIRED_THRESHOLD = 1000;
/** prepare_game caps tiredness at this value (before the stims are applied). */
export const TIRED_MAX = 3599;
/** prepare_game caps every *_attempt counter at this value. */
export const ATTEMPT_CAP = 50;
/** FWP.C_BLUE: the player's team colour. */
export const PLAYER_TEAM_COLOR = 2;
/** Number of assault zones on the world map (new_interf.render "assault": terr_zone_1 .. terr_zone_17). */
export const ASSAULT_ZONES = 17;
/**
 * Sanity bound of the graves of one outcome (the largest stage has 13 enemy worms). end_game keeps them all, as the
 * original does; the grave search shows the first 16 (new_interf.init_grave_search: gsearch_1 .. gsearch_16).
 */
export const MAX_GRAVES = 64;
/** Highest team number of a battle (the fight screen lists team1 .. team4). */
export const MAX_TEAM = 4;

/** Reason codes of the battle ops (RuleError.reason), besides the common 'bad_args', 'unknown_item', 'level_too_low'. */
export const BATTLE_REASONS = [
  /** The stage is beyond the mode's progress (min(player *_max, TEAMS_DEF *_max)): its button is not listed. */
  'stage_locked',
  /** The last stage of missions / survival / boss is "in development" (TEAMS_DEF *_max): 'go' is hidden. */
  'stage_not_ready',
  /** A boss the player has already beaten ("Вы уже победили этого босса"). */
  'already_won',
  /** Tiredness plus the stims' tiredness is TIRED_THRESHOLD or more ("Вы очень устали"). */
  'too_tired',
  /** The assault zone belongs to the player. */
  'zone_owned',
  /** The assault zone belongs to the player's clan. */
  'zone_clan_owned',
  /** battle.result: the outcome is not the battle of the reward basis (another mode, stage or zone). */
  'battle_mismatch',
  /**
   * battle.result: the mode's stage progress (*_max / *_attempt) is no longer what the start left (progress.startOver
   * or another start in between); the win gives nothing, like an abandoned battle.
   */
  'battle_state_changed',
] as const;

// ------------------------------------------------------------------------------------------------ types

/** Battle modes that battle.start accepts (a 'just' battle changes no state and has no start op). */
export type BattleStartMode = StageMode;
export const BATTLE_START_MODES: readonly BattleStartMode[] = ['career', 'missions', 'survival', 'boss', 'assault'];
/** Modes a finished battle can have. */
export type BattleMode = StageMode | 'just';
export const BATTLE_MODES: readonly BattleMode[] = [...BATTLE_START_MODES, 'just'];

/** Who holds an assault zone, from the attacker's point of view (the assault service knows it). */
export type ZoneOwner = 'none' | 'other' | 'self' | 'clan';
export const ZONE_OWNERS: readonly ZoneOwner[] = ['none', 'other', 'self', 'clan'];

export interface BattleStartArgs {
  mode: BattleStartMode;
  /** career / missions / survival / boss: the stage (new_interf.cur_element on the 'fight' screen). */
  stage?: number;
  /** assault: the zone 1..17 (cur_element on the 'assault' screen). */
  zone?: number;
  /** assault: the zone's capture count (ASSAULT[zone].zone_stage -> FWP._play_zone_stage; enemy strength). */
  zoneStage?: number;
  /** assault: who holds the zone (the screen hides 'assault_fight' for the own and the own clan's zones). */
  zoneOwner?: ZoneOwner;
  /** Annihilators of the auction for this battle (win_auc_weapons + auc_weapons; default 0). */
  annihilators?: number;
  /** Display name of the player's team (init_game: user.name; default ''). */
  teamName?: string;
}

export interface BattleTeamOptions {
  mode: BattleStartMode;
  /** The stage, or the zone for an assault (FWP._play_id). */
  stage: number;
  zoneStage?: number;
  /** Stims taken into the battle (FWP.saved_stims). */
  stims: Record<string, number>;
  annihilators?: number;
  teamName?: string;
}

/**
 * Everything the battle reads from the player (a BattleInit can be built from it without the profile):
 *  - `team`: init_game's team object for proc.init_gameplay (weapons as copies per rank 0..10, worm count and the
 *    worms' attack / protect / health / spec / perks after get_affected_skills);
 *  - the player fields proc / obj read during the battle (perks, spec, tired, cur_impl, settings.skill, cur_rip) and
 *    the fields the battle screen shows (cur_flag, worm names);
 *  - the arsenal with ranks, stolen and crafted flags (player.weapons as stored).
 */
export interface BattleTeam {
  mode: BattleStartMode;
  stage: number;
  /** assault only. */
  zoneStage?: number;
  team: {
    weapons: Record<string, number[]>;
    worms: number;
    color: number;
    name: string;
    cpu_level: number;
    [worm: string]: any;
  };
  /** get_affected_skills of the player with the stims taken into the battle. */
  skills: AffectedSkills;
  stims: Record<string, number>;
  perks: Record<string, number>;
  spec: number;
  tired: number;
  impl: number;
  /** settings.skill: difficulty 0..4, +10 in hardcore mode. */
  difficulty: number;
  rip: number;
  flag: number;
  /** player.worm_name1 .. worm_name<worms> as stored ('' = the default name; FWP.get_player_worm_name). */
  wormNames: unknown[];
  /** player.weapons: [count, {range, stolen, crafted}, ...] per weapon id. */
  arsenal: Record<string, any[]>;
}

export interface BattleStartResult {
  mode: BattleStartMode;
  /** The stage, or the zone for an assault. */
  stage: number;
  zoneStage?: number;
  /** The battle seed (proc.init_gameplay: FWP.cur_random = true_random(0, 399)). */
  seed: number;
  /** player.stim_to_fight as it was: the stims this battle uses (FWP.saved_stims); removed from the state. */
  stimsInBattle: Record<string, number>;
  /** Tiredness change of the start (prepare_game + the time refresh). */
  tired: { before: number; after: number };
  /** Papa John is in town this hour (FWP.PAPA_HERE after the refresh; memory only in the game). */
  papaHere: boolean;
  team: BattleTeam;
  /** What the reward depends on, frozen now: the backend keeps it with the in-flight battle for applyBattleResult. */
  basis: BattleRewardBasis;
}

/**
 * The player inputs of a battle's reward (FWP.end_game_fn + check_new_level), as they were right after the start
 * (applyBattleStart; battleRewardBasis). JSON-serializable. In the original none of them could change between the start
 * and the end of a battle, so applyBattleResult reads them from here instead of the state at the finish.
 */
export interface BattleRewardBasis {
  mode: BattleStartMode;
  /** The stage, or the zone for an assault. */
  stage: number;
  /**
   * settings.skill: difficulty 0..4, +10 in hardcore mode (skill / hardcore multipliers, task 23 -> 24). null when it
   * is not a finite number (the NaN of the original's 'skill_attack' defect, a missing value): every multiplier is 1
   * for it and the task check fails, exactly as for NaN.
   */
  skill: number | null;
  /** player.perks (champion, more_exp; education, legioner and surprise of a level-up). */
  perks: Record<string, number>;
  /** player.spec (spec 4 = more_exp level 3). */
  spec: number;
  /** career / missions / survival / boss: player.<mode>_max after the start (the stage multiplier and its base). */
  max?: number;
  /** career / missions / survival / boss: player.<mode>_attempt after the start (1 = the first attempt). */
  attempt?: number;
}

/** end_game_fn's inputs: proc's battle counters and graves (the validator reports them). */
export interface BattleOutcome {
  /** Team number that won (1 = the player; 0 = cancelled / nobody). */
  winner: number;
  mode: BattleMode;
  /** career / missions / survival / boss: the stage. */
  stage?: number;
  /** assault: the zone. */
  zone?: number;
  /** proc.ach_killed: enemy worms the player killed, by the killing weapon's type. */
  ach_killed?: { p: number; e: number; a: number; c: number; h: number; f: number; l: number };
  ach_paralyzed?: number;
  ach_annihilated?: number;
  /** A worm of the player was injured (proc.ach_injure). */
  ach_injure?: boolean;
  /** A worm of the player died (proc.ach_kill). */
  ach_kill?: boolean;
  /** Enemy graves left on the map (end_game_fn: 'G' objects with wteam != 1), in object order. */
  graves?: BattleGrave[];
}

export interface BattleResultResult {
  win: boolean;
  /** FWP.end_game as end_game_fn builds it (win / new level / surprise screens, grave search). */
  endGame: EndGame;
  /** What the battle granted (a flat view of endGame). */
  granted: {
    gold: number;
    exp: number;
    /** rating_bonus of the win plus 100 per level gained (check_new_level). */
    rating: number;
    levels: number;
    diam: number;
    skillPts: number;
    perkPts: number;
    /** Surprise perk weapons, one per level gained (may repeat across levels, never twice in a row). */
    weapons: string[];
  };
  /** Enemy graves for the grave search (endGame.graves as a list). */
  graves: BattleGrave[];
  /** The player's level afterwards (derived from exp). */
  level: number;
}

// ------------------------------------------------------------------------------------------------ helpers

const MAX_FIELD: Record<Exclude<StageMode, 'assault'>, 'career_max' | 'missions_max' | 'survival_max' | 'boss_max'> = {
  career: 'career_max',
  missions: 'missions_max',
  survival: 'survival_max',
  boss: 'boss_max',
};

/** player.<mode>_attempt of a stage mode. */
function attemptField(mode: Exclude<StageMode, 'assault'>): 'career_attempt' | 'missions_attempt' | 'survival_attempt' | 'boss_attempt' {
  return (mode + '_attempt') as 'career_attempt' | 'missions_attempt' | 'survival_attempt' | 'boss_attempt';
}

/**
 * The reward basis of a battle of `mode` at `stage` (the zone for an assault) from the state right after its start
 * (applyBattleStart builds it this way and returns it as `basis`).
 */
export function battleRewardBasis(state: WorkingState, mode: BattleStartMode, stage: number): BattleRewardBasis {
  const p = state.player;
  const skill = p.settings ? p.settings.skill : undefined;
  // end_game_fn only tests `spec == 4`: a missing spec (NaN) behaves like 0.
  const spec = Number(p.spec);
  const basis: BattleRewardBasis = {
    mode,
    stage,
    skill: typeof skill === 'number' && Number.isFinite(skill) ? skill : null,
    perks: clone(p.perks ?? {}),
    spec: Number.isFinite(spec) ? spec : 0,
  };
  if (mode !== 'assault') {
    basis.max = p[MAX_FIELD[mode]];
    basis.attempt = p[attemptField(mode)];
  }
  return basis;
}

/** The TEAMS_DEF stage of a battle; RuleError('unknown_item') when the table has none. */
function teamDefOrThrow(defs: GameDefs, mode: StageMode, id: number): any {
  const td = battleTeamDef(defs, mode, id);
  if (!td) throw new RuleError('unknown_item', { field: mode === 'assault' ? 'zone' : 'stage', mode, id });
  return td;
}

/** Every stim taken into the battle must be a STIM_DEF item (prepare_game / get_affected_skills crash otherwise). */
function checkStims(defs: GameDefs, stims: Record<string, number>): void {
  for (const k of keys(stims)) {
    if (!Object.prototype.hasOwnProperty.call(defs.STIM_DEF, k)) throw new RuleError('bad_state', { field: 'stim_to_fight', id: k });
  }
}

/** The render "fight" / render "assault" conditions for the start button (see BATTLE_REASONS). */
function checkBattleStart(p: PlayerState, a: BattleStartArgs, defs: GameDefs): void {
  checkStims(defs, p.stim_to_fight);
  if (a.mode === 'assault') {
    const zone = a.zone!;
    // The screen lists zones 1..17 and shows 'assault_fight' only for cur_element > 0.
    check(zone >= 1 && zone <= ASSAULT_ZONES, 'unknown_item', { field: 'zone', id: zone });
    teamDefOrThrow(defs, 'assault', zone);
    // ASSAULT[z].user_id != user_id && (return_clan(owner) != user_clan || user_clan == 0).
    if (a.zoneOwner === 'self') throw new RuleError('zone_owned', { zone });
    if (a.zoneOwner === 'clan') throw new RuleError('zone_clan_owned', { zone });
    // No tiredness check: the assault screen has none (new_interf.render "assault").
    return;
  }
  const mode = a.mode;
  const stage = a.stage!;
  const td = teamDefOrThrow(defs, mode, stage);
  const maxField = MAX_FIELD[mode];
  // init "fight": the stage buttons 1..min(player *_max, TEAMS_DEF *_max).
  const available = Math.min(p[maxField], defs.TEAMS_DEF[maxField]);
  if (!(stage <= available)) throw new RuleError('stage_locked', { mode, stage, available });
  // render "fight": the last missions / survival / boss stage is "in development".
  if (mode !== 'career' && stage == defs.TEAMS_DEF[maxField]) throw new RuleError('stage_not_ready', { mode, stage });
  // render "fight" boss: "Вы уже победили этого босса".
  if (mode === 'boss' && stage < p.boss_max) throw new RuleError('already_won', { mode, stage });
  // render "fight" missions / boss: TEAMS_DEF level <= player.level.
  if ((mode === 'missions' || mode === 'boss') && !(td.level <= p.level)) throw new RuleError('level_too_low', { mode, stage, have: p.level, need: td.level });
  // render "fight": get_affected_skills(player with stim_to_fight).tired >= TIRED_THRESHOLD hides 'go'.
  const skills = affectedSkills(defs, { attack: p.attack, protect: p.protect, health: 100 + 50 * p.health, tired: p.tired, stim_to_fight: p.stim_to_fight, impl: p.cur_impl });
  if (skills.tired >= TIRED_THRESHOLD) throw new RuleError('too_tired', { tired: skills.tired, threshold: TIRED_THRESHOLD });
}

// ------------------------------------------------------------------------------------------------ battle.start

/**
 * FWP.init_game's team for proc.init_gameplay plus the player fields the battle reads (see BattleTeam), from the
 * state as init_game sees it: after applyBattleStart (tiredness after the start and the time refresh, stims already
 * removed from stim_to_fight and passed in `opts.stims`).
 *
 * Kept quirk (a rule): get_affected_skills gets player.tired, which already contains the stims' tiredness
 * (prepare_game added it), together with the stims again, so the tiredness penalty counts the stims' (negative)
 * tiredness twice (FWP.ts init_game `_loc4_`).
 */
export function buildBattleTeam(state: WorkingState, opts: BattleTeamOptions, defs: GameDefs): BattleTeam {
  const p = state.player;
  const td = teamDefOrThrow(defs, opts.mode, opts.stage);
  checkStims(defs, opts.stims);
  const team: BattleTeam['team'] = { weapons: {}, worms: 0, color: PLAYER_TEAM_COLOR, name: opts.teamName ?? '', cpu_level: 0 };
  team.worms = Math.min(2 + p.worms, td.worm_limit);
  const skills = affectedSkills(defs, {
    attack: p.attack,
    protect: p.protect,
    health: 100 + 50 * p.health,
    worms: 2 + p.worms,
    tired: p.tired,
    stim_to_fight: opts.stims,
    impl: p.cur_impl,
  });
  for (let i = 1; i <= team.worms; i++) {
    const worm: any = { attack: skills.attack, protect: skills.protect, health: skills.health, spec: p.spec, perks: {}, name: 'Worm ' + i };
    for (const k of keys(p.perks)) worm.perks[k] = p.perks[k];
    team['worm' + i] = worm;
  }
  for (const id of keys(p.weapons)) {
    const counts = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let j = 1; j <= p.weapons[id][0]; j++) ++counts[p.weapons[id][j].range];
    team.weapons[id] = counts;
  }
  const annihilators = opts.annihilators ?? 0;
  if (annihilators != 0) team.weapons['annihilator'] = [0, annihilators, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const wormNames: unknown[] = [];
  for (let i = 1; i <= team.worms; i++) wormNames.push(p['worm_name' + i]);
  return {
    mode: opts.mode,
    stage: opts.stage,
    ...(opts.mode === 'assault' ? { zoneStage: opts.zoneStage ?? 0 } : {}),
    team,
    skills,
    stims: clone(opts.stims),
    perks: clone(p.perks),
    spec: p.spec,
    tired: p.tired,
    impl: p.cur_impl,
    difficulty: p.settings.skill,
    rip: p.cur_rip,
    flag: p.cur_flag,
    wormNames,
    arsenal: clone(p.weapons),
  };
}

/**
 * battle.start: checks the start button's conditions, then applies FWP.prepare_game and the time refresh of
 * net_check_auc, builds the battle team and draws the seed (ctx.rng.int(0, 399), the first random number the
 * original draws after 'go'). The arguments are validated here too (RuleError 'bad_args'), so a direct call from the
 * backend is as strict as the op.
 *
 * The time refresh is part of the start (the original subtracts the rest time after adding the battle's tiredness,
 * and the clamps make the order matter): do not run time.refresh on the state right before it.
 */
export function applyBattleStart(state: WorkingState, rawArgs: BattleStartArgs, ctx: RuleContext): OpOutput<BattleStartResult> {
  const args = parseBattleStartArgs(rawArgs);
  const p = state.player;
  const defs = ctx.defs;
  checkBattleStart(p, args, defs);
  const mode = args.mode;
  const playId = mode === 'assault' ? args.zone! : args.stage!;
  const tiredBefore = p.tired;

  // FWP.prepare_game
  let lessTired = 0;
  if (p.perks['less_tired']) lessTired = perkEffect('less_tired', p.perks.less_tired, '');
  if (p.spec == 3) lessTired = perkEffect('less_tired', 3, '');
  p.tired += TIRED_INC - lessTired;
  if (mode == 'career') ++p.career_attempt;
  if (mode == 'missions') ++p.missions_attempt;
  if (mode == 'survival') ++p.survival_attempt;
  if (mode == 'boss') ++p.boss_attempt;
  if (p.tired > TIRED_MAX) p.tired = TIRED_MAX;
  if (p.career_attempt > ATTEMPT_CAP) p.career_attempt = ATTEMPT_CAP;
  if (p.missions_attempt > ATTEMPT_CAP) p.missions_attempt = ATTEMPT_CAP;
  if (p.survival_attempt > ATTEMPT_CAP) p.survival_attempt = ATTEMPT_CAP;
  if (p.boss_attempt > ATTEMPT_CAP) p.boss_attempt = ATTEMPT_CAP;
  let stimCount = 0;
  for (const k of keys(p.stim_to_fight)) {
    p.tired += defs.STIM_DEF[k].tired * p.stim_to_fight[k];
    stimCount += p.stim_to_fight[k];
  }
  p.achiev['stims'] += stimCount;
  if (stimCount > 1 && p.cur_task == 6) p.cur_task = 7;
  if (p.tired < 0) p.tired = 0;
  const stimsInBattle = clone(p.stim_to_fight);
  p.stim_to_fight = {};

  // continue_preparing_game -> net_check_auc -> net_refresh_time (then the 'battle_start' save)
  const { papaHere } = refreshTime(p, ctx);

  // init_game -> proc.init_gameplay
  const zoneStage = mode === 'assault' ? args.zoneStage ?? 0 : undefined;
  const team = buildBattleTeam(state, { mode, stage: playId, zoneStage, stims: stimsInBattle, annihilators: args.annihilators, teamName: args.teamName }, defs);
  const seed = ctx.rng.int(0, 399);
  return {
    state,
    result: {
      mode,
      stage: playId,
      ...(zoneStage !== undefined ? { zoneStage } : {}),
      seed,
      stimsInBattle,
      tired: { before: tiredBefore, after: p.tired },
      papaHere,
      team,
      basis: battleRewardBasis(state, mode, playId),
    },
  };
}

// ------------------------------------------------------------------------------------------------ battle.result

/**
 * battle.result: FWP.end_game_fn(winner) for a battle started with applyBattleStart. `state` is the current state (the
 * start's mutations included) and `basis` the BattleRewardBasis that start returned (null only for a 'just' battle,
 * which has no start). A PvE win (career / missions / survival / boss) gives
 *   today_wins++, rating += 1 + get_rating_inc, the kill / no_death / no_injure / para / annih achievements, the
 *   mode's task step, the stage multiplier m (1.2 for the first attempt at the newest stage, 0.15 for a stage already
 *   passed), *_max++ and *_attempt = 0 for the newest stage, gold += round(TD.gold * m * champion * difficulty *
 *   hardcore), exp += round(TD.exp * m * more_exp (spec 4 = level 3) * difficulty * hardcore), then check_new_level;
 * an assault win gives the same counters and task 28 -> 29, gold/exp without m and without difficulty, then
 * achiev.assault++. A loss (winner != 1), a cancelled battle (0) and any 'just' battle change nothing.
 *
 * Difficulty, perks and spec come from `basis` (the original read them from the player at the end of the battle, when
 * they could not have changed since the start). The stage progress must still be the start's (RuleError
 * 'battle_state_changed' for a win otherwise: only progress.startOver or another start can change it in between), and
 * the outcome must be the basis's battle ('battle_mismatch'). The outcome and the basis are validated here too
 * (RuleError 'bad_args'): every counter of a win is required.
 */
export function applyBattleResult(state: WorkingState, outcome: BattleOutcome, ctx: RuleContext, basis: BattleRewardBasis | null): OpOutput<BattleResultResult> {
  const o = parseBattleOutcome(outcome);
  const b = parseRewardBasis(basis, o.mode);
  const p = state.player;
  const defs = ctx.defs;
  const mode = o.mode;
  const playId = mode === 'assault' ? o.zone! : mode === 'just' ? o.stage ?? 0 : o.stage!;
  const td = mode === 'just' ? undefined : teamDefOrThrow(defs, mode, playId);
  const win = o.winner == 1;
  if (b) {
    if (b.mode !== mode || b.stage !== playId) throw new RuleError('battle_mismatch', { basis: { mode: b.mode, stage: b.stage }, outcome: { mode, stage: playId } });
    // applyBattleStart never starts a stage beyond the mode's progress (checkBattleStart 'stage_locked').
    if (mode !== 'assault' && !(playId <= b.max!)) throw new RuleError('battle_mismatch', { basis: { mode: b.mode, stage: b.stage, max: b.max } });
    if (win && mode !== 'assault') {
      const maxField = MAX_FIELD[mode];
      const attempt = attemptField(mode);
      if (p[maxField] != b.max || p[attempt] != b.attempt) {
        throw new RuleError('battle_state_changed', { mode, start: { max: b.max, attempt: b.attempt }, now: { max: p[maxField], attempt: p[attempt] } });
      }
    }
  }

  const graves: any[] = [0, {}];
  for (const g of o.graves ?? []) {
    ++graves[0];
    graves[graves[0]] = { name: g.name, cpu_level: g.cpu_level };
  }
  const endGame: EndGame = {
    win,
    exp_bonus: 0,
    gold_bonus: 0,
    rating_bonus: 0,
    levels_upg: 0,
    diam_bonus: 0,
    skill_pts: 0,
    perk_pts: 0,
    surprise_weapons: [0, ''],
    game_stage: playId,
    game_mode: mode,
    graves,
  };

  if (o.winner == 1 && mode != 'just') {
    // A rewarded outcome always has a basis (parseRewardBasis requires it for every mode but 'just').
    const start = b!;
    const killed = clone(o.ach_killed!);
    ++p.today_wins;
    endGame.rating_bonus = 1 + ratingInc(p.today_wins, p.yest_wins);
    p.rating += endGame.rating_bonus;
    p.achiev['kill_p'] += killed['p'];
    p.achiev['kill_e'] += killed['e'];
    p.achiev['kill_a'] += killed['a'];
    p.achiev['kill_c'] += killed['c'];
    p.achiev['kill_h'] += killed['h'];
    p.achiev['kill_f'] += killed['f'];
    p.achiev['kill_l'] += killed['l'];
    p.achiev['no_death'] += o.ach_kill ? 0 : 1;
    p.achiev['no_injure'] += Boolean(o.ach_kill) || Boolean(o.ach_injure) ? 0 : 1;
    p.achiev['para'] += o.ach_paralyzed!;
    p.achiev['annih'] += o.ach_annihilated!;
    if (mode == 'career' && p.cur_task == 1) p.cur_task = 2;
    if (mode == 'career' && (start.skill == 4 || start.skill == 14) && p.cur_task == 23) p.cur_task = 24;
    if (mode == 'survival' && p.cur_task == 5) p.cur_task = 6;
    if (mode == 'survival' && p.cur_task == 12) p.cur_task = 13;
    if (mode == 'missions' && p.cur_task == 8) p.cur_task = 9;
    if (mode == 'missions' && p.cur_task == 14) p.cur_task = 15;
    if (mode == 'boss' && p.cur_task == 18) p.cur_task = 19;
    if (mode == 'boss' && p.cur_task == 26) p.cur_task = 27;
    if (mode == 'assault' && p.cur_task == 28) p.cur_task = 29;

    let base = 100;
    let m = 1;
    if (mode != 'assault') {
      // The progress equals the start's (checked above): *_max / *_attempt as the start left them.
      const maxField = MAX_FIELD[mode];
      const attempt = attemptField(mode);
      if (p[attempt] == 1 && playId == p[maxField]) m = 1.2;
      if (playId < p[maxField]) m = 0.15;
      base = p[maxField];
      if (p[maxField] == playId) {
        ++p[maxField];
        p[attempt] = 0;
      }
    }
    const perks = start.perks;
    let champion = 1;
    if (perks['champion']) champion = perkEffect('champion', perks.champion, '');
    let moreExp = 1;
    if (perks['more_exp']) moreExp = perkEffect('more_exp', perks.more_exp, '');
    if (start.spec == 4) moreExp = perkEffect('more_exp', 3, '');
    let gold: number;
    let exp: number;
    if (mode != 'assault') {
      const skill = start.skill;
      gold = Math.round(td.gold * m * champion * skillMulti(skill, 'gold') * hardcoreMulti(skill, playId, base));
      exp = Math.round(td.exp * m * moreExp * skillMulti(skill, 'exp') * hardcoreMulti(skill, playId, base));
    } else {
      gold = Math.round(td.gold * champion);
      exp = Math.round(td.exp * moreExp);
    }
    p.gold = int(p.gold + gold);
    p.exp = int(p.exp + exp);
    endGame.gold_bonus = gold;
    endGame.exp_bonus = exp;
    checkNewLevel(p, endGame, ctx, perks);
  }
  if (mode == 'assault' && Boolean(endGame.win)) ++p.achiev['assault'];

  const weapons: string[] = [];
  for (let i = 1; i <= endGame.surprise_weapons[0]; i++) weapons.push(endGame.surprise_weapons[i]);
  const graveList: BattleGrave[] = [];
  for (let i = 1; i <= graves[0]; i++) graveList.push({ name: graves[i].name, cpu_level: graves[i].cpu_level });
  return {
    state,
    result: {
      win,
      endGame,
      granted: {
        gold: endGame.gold_bonus,
        exp: endGame.exp_bonus,
        rating: endGame.rating_bonus + RATING_PER_LEVEL * endGame.levels_upg,
        levels: endGame.levels_upg,
        diam: endGame.diam_bonus,
        skillPts: endGame.skill_pts,
        perkPts: endGame.perk_pts,
        weapons,
      },
      graves: graveList,
      level: p.level,
    },
  };
}

// ------------------------------------------------------------------------------------------------ arguments

function bad(field: string, problem: string): never {
  throw new RuleError('bad_args', { field, problem });
}

function forbid(o: Record<string, unknown>, fields: readonly string[], why: string): void {
  for (const f of fields) if (o[f] !== undefined) bad(f, why);
}

function optionalBool(o: Record<string, unknown>, field: string): boolean | undefined {
  const v = o[field];
  if (v === undefined) return undefined;
  if (typeof v !== 'boolean') bad(field, 'must be a boolean');
  return v;
}

/** The stage / zone fields of a battle mode (shared by both ops). */
function parseStage(o: Record<string, unknown>, mode: BattleMode): { stage?: number; zone?: number } {
  if (mode === 'assault') {
    forbid(o, ['stage'], 'an assault has a zone, not a stage');
    return { zone: intArg(o, 'zone', { min: 1, max: ASSAULT_ZONES }) };
  }
  forbid(o, ['zone'], 'only an assault has a zone');
  if (mode === 'just') {
    const stage = optionalIntArg(o, 'stage', { min: 0, max: 1000 });
    return stage === undefined ? {} : { stage };
  }
  return { stage: intArg(o, 'stage', { min: 1, max: 1000 }) };
}

export function parseBattleStartArgs(raw: unknown): BattleStartArgs {
  const o = argsObject(raw, ['mode', 'stage', 'zone', 'zoneStage', 'zoneOwner', 'annihilators', 'teamName']);
  const mode = stringArg<BattleStartMode>(o, 'mode', { oneOf: BATTLE_START_MODES });
  const out: BattleStartArgs = { mode, ...parseStage(o, mode) };
  if (mode === 'assault') {
    out.zoneStage = intArg(o, 'zoneStage', { min: 0, max: 1_000_000_000 });
    out.zoneOwner = stringArg<ZoneOwner>(o, 'zoneOwner', { oneOf: ZONE_OWNERS });
  } else forbid(o, ['zoneStage', 'zoneOwner'], 'only an assault has a zone');
  const annihilators = optionalIntArg(o, 'annihilators', { min: 0, max: 1000 });
  if (annihilators !== undefined) out.annihilators = annihilators;
  const teamName = optionalStringArg(o, 'teamName', { maxLength: 64 });
  if (teamName !== undefined) out.teamName = teamName;
  return out;
}

const KILL_TYPES = ['p', 'e', 'a', 'c', 'h', 'f', 'l'] as const;
/** Upper bound of one battle counter (a sanity limit; real battles stay far below). */
const MAX_COUNTER = 10_000;

export function parseBattleOutcome(raw: unknown): BattleOutcome {
  const o = argsObject(raw, ['winner', 'mode', 'stage', 'zone', 'ach_killed', 'ach_paralyzed', 'ach_annihilated', 'ach_injure', 'ach_kill', 'graves']);
  const winner = intArg(o, 'winner', { min: 0, max: MAX_TEAM });
  const mode = stringArg<BattleMode>(o, 'mode', { oneOf: BATTLE_MODES });
  const out: BattleOutcome = { winner, mode, ...parseStage(o, mode) };
  // The counters matter only for a win (end_game_fn reads them nowhere else): required then, optional otherwise.
  const rewarded = winner === 1 && mode !== 'just';
  if (o.ach_killed !== undefined || rewarded) {
    if (o.ach_killed === undefined) bad('ach_killed', 'required');
    const k = argsObject(o.ach_killed, KILL_TYPES);
    const killed: any = {};
    for (const t of KILL_TYPES) killed[t] = intArg(k, t, { min: 0, max: MAX_COUNTER });
    out.ach_killed = killed;
  }
  for (const f of ['ach_paralyzed', 'ach_annihilated'] as const) {
    const v = rewarded ? intArg(o, f, { min: 0, max: MAX_COUNTER }) : optionalIntArg(o, f, { min: 0, max: MAX_COUNTER });
    if (v !== undefined) out[f] = v;
  }
  for (const f of ['ach_injure', 'ach_kill'] as const) {
    const v = optionalBool(o, f);
    if (v === undefined && rewarded) bad(f, 'required');
    if (v !== undefined) out[f] = v;
  }
  if (o.graves !== undefined) {
    if (!Array.isArray(o.graves)) bad('graves', 'must be an array');
    if (o.graves.length > MAX_GRAVES) bad('graves', `must have at most ${MAX_GRAVES} entries`);
    out.graves = o.graves.map((g, i) => {
      try {
        const e = argsObject(g, ['name', 'cpu_level']);
        return { name: stringArg(e, 'name', { maxLength: 64 }), cpu_level: intArg(e, 'cpu_level', { min: 0, max: 10 }) };
      } catch (err) {
        if (err instanceof RuleError) bad(`graves.${i}${err.details.field ? '.' + String(err.details.field) : ''}`, String(err.details.problem));
        throw err;
      }
    });
  }
  return out;
}

/**
 * The reward basis of battle.result (server data the backend kept with the in-flight battle): required for every
 * mode but 'just', which has none (null / undefined). Shape errors -> RuleError('bad_args', { field: 'basis...' }).
 */
export function parseRewardBasis(raw: unknown, mode: BattleMode): BattleRewardBasis | null {
  if (mode === 'just') {
    if (raw !== null && raw !== undefined) bad('basis', "a 'just' battle has no start and no reward basis");
    return null;
  }
  if (raw === null || raw === undefined) bad('basis', 'required: the basis battle.start returned');
  let o: Record<string, unknown>;
  try {
    o = argsObject(raw, ['mode', 'stage', 'skill', 'perks', 'spec', 'max', 'attempt']);
  } catch (err) {
    if (err instanceof RuleError) bad(`basis${err.details.field ? '.' + String(err.details.field) : ''}`, String(err.details.problem));
    throw err;
  }
  const field = (f: string) => 'basis.' + f;
  const num = (f: string, v: unknown): number => {
    if (typeof v !== 'number' || !Number.isFinite(v)) bad(field(f), 'must be a finite number');
    return v;
  };
  const bmode = o.mode;
  if (typeof bmode !== 'string' || !(BATTLE_START_MODES as readonly string[]).includes(bmode)) bad(field('mode'), `must be one of ${BATTLE_START_MODES.join(', ')}`);
  const stage = num('stage', o.stage);
  const skill = o.skill === null ? null : num('skill', o.skill);
  if (!o.perks || typeof o.perks !== 'object' || Array.isArray(o.perks)) bad(field('perks'), 'must be an object');
  const perks: Record<string, number> = {};
  for (const [k, v] of Object.entries(o.perks as Record<string, unknown>)) perks[k] = num(`perks.${k}`, v);
  const out: BattleRewardBasis = { mode: bmode as BattleStartMode, stage, skill, perks, spec: num('spec', o.spec) };
  if (bmode === 'assault') {
    for (const f of ['max', 'attempt']) if (o[f] !== undefined) bad(field(f), 'an assault has no stage progress');
  } else {
    out.max = num('max', o.max);
    out.attempt = num('attempt', o.attempt);
  }
  return out;
}

export interface BattleResultArgs {
  /** The verified outcome (or the claim of an unverified battle). */
  outcome: BattleOutcome;
  /** BattleStartResult.basis of the battle (null / absent only for a 'just' battle). */
  basis: BattleRewardBasis | null;
}

export function parseBattleResultArgs(raw: unknown): BattleResultArgs {
  const o = argsObject(raw, ['outcome', 'basis']);
  const outcome = parseBattleOutcome(o.outcome);
  return { outcome, basis: parseRewardBasis(o.basis, outcome.mode) };
}

// ------------------------------------------------------------------------------------------------ ops

export const battleOps = {
  /** battle.start { mode, stage | zone + zoneStage + zoneOwner, annihilators?, teamName? } (server-side, see the file comment). */
  'battle.start': defineOp<BattleStartArgs, BattleStartResult>({
    parseArgs: parseBattleStartArgs,
    run: applyBattleStart,
  }),
  /**
   * battle.result { outcome: { winner, mode, stage | zone, ach_*, graves }, basis } with the verified outcome and the
   * reward basis of its battle.start (server-side).
   */
  'battle.result': defineOp<BattleResultArgs, BattleResultResult>({
    parseArgs: parseBattleResultArgs,
    run: (state, args, ctx) => applyBattleResult(state, args.outcome, ctx, args.basis),
  }),
};
